import {
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  ReactNode,
  createContext,
} from "react";
import BottomPanel from "../layout/BottomPanel";
import LoadingText from "../shared/LoadingText";
import { Schema } from "@vehicle-contract/common";
import { SchemaType } from "@vehicle-contract/common/lib/model/contract/SchemaType";
import { captureException } from "@sentry/core";
import { useContractClient } from "../api/ContractClient";
import { ContractViewModel, FieldValue } from "./model/ContractViewModel";
import { ContractType } from "./model/ContractViewModel";
import { Role } from "./model/ContractViewModel";
import { ApiError } from "../api/ApiClient";
import { useLoadContractContext } from "../LoadContract";
import { ButtonLink } from "@teamscheme/button-link";
import { HeaderWithProgressContext } from "../layout/header/HeaderWithProgress";

export const CONTRACT_TYPES = {
  None: ContractType.None,
  Simple: ContractType.Simple,
  Standard: ContractType.Standard,
  Member: ContractType.Member,
};

export function contractTypeName(type?: ContractType) {
  if (type === null || type === undefined) return null;

  return Object.entries(CONTRACT_TYPES).filter(([_, id]) => id === type)[0][0];
}

export interface IContractContext {
  contract: ContractViewModel | null;
  role: Role | null;
  schemaType?: SchemaType;
  isInitiator?: boolean;
  applyContractUpdates(
    updates: (prev: ContractViewModel) => ContractViewModel
  ): void;
  onChange(contract: ContractViewModel): void;
  saveField(
    section: string,
    field: string,
    value:
      | RawFieldValue
      | FieldValue /* Conditoon questions are saved as FieldValue */,
    fieldValue: FieldValue | null,
    update?: CustomUpdateCallback
  ): Promise<void>;
  saveFields(fields: FieldSave[]): Promise<void>;
  enqueueChanges(fields: FieldSave[]): void;
}

const ContractContext = createContext<IContractContext | null>(null);

export function useRole() {
  const contractContext = useContext(ContractContext);

  if (!contractContext) throw new Error("No ContractContext found!");

  const { role } = contractContext;

  if (role === null) throw new Error("No Role loaded!");

  return role;
}

export function useContract() {
  const contractContext = useContext(ContractContext);

  if (!contractContext) throw new Error("No ContractContext found!");

  const { contract } = contractContext;

  if (!contract) throw new Error("No Contract found!");

  return contract;
}

export function useContractContext() {
  const contractContext = useContext(ContractContext);

  if (!contractContext) throw new Error("No ContractContext found!");

  return contractContext;
}

export function useSchema() {
  const { schemaType } = useContract();

  return useMemo(() => new Schema(schemaType), [schemaType]);
}

export const ROLE_NAMES = {
  [Role.Buyer]: "kjøper",
  [Role.Seller]: "selger",
};

export function capitalizeFirst(str: string) {
  return str.substring(0, 1).toUpperCase() + str.substring(1);
}

export function useRoleName() {
  const role = useRole();
  const otherRole = useOtherRole();

  return useMemo(() => {
    const self = ROLE_NAMES[role];
    const other = ROLE_NAMES[otherRole];

    return {
      Other: capitalizeFirst(other),
      other,
      Self: capitalizeFirst(self),
      self,
    };
  }, [role, otherRole]);
}

export function useOtherRole() {
  const role = useRole();
  return role === Role.Seller ? Role.Buyer : Role.Seller;
}

export function useIsInitiator() {
  const contractContext = useContext(ContractContext);

  if (!contractContext) throw new Error("No ContractContext found!");

  const { isInitiator } = contractContext;

  if (typeof isInitiator !== "boolean")
    throw new Error("No IsIntiiator loaded!");

  return isInitiator;
}

interface Deferred {
  promise: Promise<void>;
  resolve: () => void;
  reject: (error: any) => void;
}

export type RawFieldValue = string | number | boolean | null;
export type CustomUpdateCallback = (
  prev: ContractViewModel
) => ContractViewModel;

interface FieldSave {
  section: string;
  field: string;
  value: RawFieldValue;
  fieldValue: FieldValue | null;
  update?: CustomUpdateCallback;
}

interface Save {
  deferred: Deferred;
  fields: FieldSave[];
}

export function ContractProvider({ children }: { children: ReactNode }) {
  const { contract, updateContract } = useLoadContractContext();
  const [saveError, setSaveError] = useState<ApiError | null>(null);
  const [saving, setSaving] = useState(false);
  const client = useContractClient();
  const headerWithProgressContext = useContext(HeaderWithProgressContext);
  if (!headerWithProgressContext)
    throw new Error("No HeaderWithProgressContext found!");
  const {
    setIsSaving: setIsSavingInHeader,
    setHasSaved: setIsSavedInHeader,
    setSaveError: setSavedErrorInHeader,
  } = headerWithProgressContext;

  useEffect(() => {
    setIsSavingInHeader(saving);
  }, [saving, setIsSavingInHeader]);

  const [saves, setSaves] = useState<Save[]>([]);
  const [queue, setQueue] = useState<FieldSave[]>([]);

  const enqueueChanges = useCallback(function enqueueChanges(
    fields: FieldSave[]
  ) {
    setQueue((prev) => [...prev, ...fields]);
  },
  []);

  const saveFields = useCallback(
    function saveFields(fields: FieldSave[]) {
      const deferred: Partial<Deferred> = {};

      deferred.promise = new Promise((resolve, reject) => {
        deferred.resolve = resolve;
        deferred.reject = reject;
      });

      setSaves((saves) => [
        ...saves.map(({ deferred, fields }) => ({
          deferred,
          fields: fields.filter(
            (a) =>
              !fields.filter(
                (b) => !(a.section === b.section && a.field === b.field)
              ).length
          ),
        })),
        { deferred: deferred as Deferred, fields: queue.concat(fields) },
      ]);
      setQueue([]);

      return deferred.promise;
    },
    [queue]
  );

  const saveField = useCallback(
    function saveField(
      section: string,
      field: string,
      value: RawFieldValue,
      fieldValue: FieldValue | null,
      update?: CustomUpdateCallback
    ) {
      return saveFields([{ section, field, value, fieldValue, update }]);
    },
    [saveFields]
  );

  const contractId = contract?.data?.id;

  useEffect(() => {
    if (!saves.length) return;

    const timeout = setTimeout(async () => {
      setSaving(true);
      const fieldSaves = saves.map(({ fields }) => fields).flat();
      const deferreds = saves.map(({ deferred }) => deferred);
      const payload = {
        updates: fieldSaves
          .filter(({ section }) => !!section)
          .reduce((acc, y) => {
            const { section, field, fieldValue } = y;
            const match = acc.filter(
              (x) => x.section === section && x.field === field
            );

            if (match.length) match[0].fieldValue = fieldValue;
            else acc.push(y);

            return acc;
          }, [] as FieldSave[])
          .map(({ section, field, fieldValue }) => ({
            name: section,
            fieldUpdate: {
              name: field,
              value: fieldValue,
            },
          })),
      };

      try {
        if (payload.updates.length) {
          await client.put(`contract/${contractId}`, payload);
        }

        setSaves([]);

        for (const deferred of deferreds) deferred.resolve();

        for (let x of fieldSaves) {
          const { section, field, value, update } = x;

          updateContract((prev) => {
            if (update) return update(prev);

            const next: ContractViewModel = {
              ...prev,
              [section]: {
                ...(prev as any)[section],
                [field]: value,
              },
            };

            return next;
          });
        }
        setSaveError(null);
        setSavedErrorInHeader(null);
        setIsSavedInHeader(true);
      } catch (error) {
        if (!error.isCanceled) {
          for (const deferred of deferreds) deferred.reject(error);
          console.error("Could not save contract changes", error);
          setSaveError(error);
          setSavedErrorInHeader(error);
          captureException(error);
        }
      } finally {
        setSaving(false);
      }
    }, 50);

    return () => {
      clearTimeout(timeout);
    };
  }, [
    saves,
    client,
    contractId,
    updateContract,
    setIsSavedInHeader,
    setSavedErrorInHeader,
  ]);

  const applyContractUpdates = useCallback(
    (updates: (prev: ContractViewModel) => ContractViewModel) =>
      updateContract((contract) => updates(contract)),
    [updateContract]
  );

  const onChange = useCallback(
    (data: ContractViewModel) => updateContract(data),
    [updateContract]
  );

  return (
    <ContractContext.Provider
      value={{
        contract: contract?.data || null,
        role: contract ? contract.role : null,
        isInitiator: contract
          ? contract.role === contract?.data?.initiator
          : undefined,
        applyContractUpdates,
        onChange,
        saveField,
        saveFields,
        enqueueChanges,
      }}
    >
      {children}
      {saveError ? (
        <BottomPanel>
          <span>Vi klarte ikke lagre endringene.</span>
          <ButtonLink
            variant="secondary"
            onClick={() => setSaves((saves) => [...saves])}
            disabled={saving}
          >
            {saving ? <LoadingText text="Lagrer" /> : "Prøv på nytt"}
          </ButtonLink>
        </BottomPanel>
      ) : null}
      {!saveError && saving ? (
        <BottomPanel>
          <LoadingText text="Lagrer" />
        </BottomPanel>
      ) : null}
    </ContractContext.Provider>
  );
}

export default ContractContext;
